/**
 * @fileoverview Homepage registration code input component
 * 
 * @purpose Reusable component for homepage that accepts registration code input
 * @usage Add to homepage to allow users to enter registration codes
 * @ownership Shell components
 * @dependencies
 * - next/navigation
 * @restrictions
 * - Client-side only
 */

'use client'

import { useRouter } from 'next/navigation'
import { useState } from 'react'

export type RegistrationCodeInputProps = {
  /** Callback when code is submitted (optional, defaults to navigation) */
  onSubmit?: (code: string) => void
  /** Placeholder text for input */
  placeholder?: string
  /** Button text */
  buttonText?: string
  /** Additional CSS classes */
  className?: string
}

export function RegistrationCodeInput({
  onSubmit,
  placeholder = 'Enter your registration code',
  buttonText = 'Register',
  className,
}: RegistrationCodeInputProps) {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!code.trim()) return

    setIsLoading(true)

    if (onSubmit) {
      onSubmit(code.trim())
    } else {
      // Default: navigate to register page with code in URL
      router.push(`/auth/register?code=${encodeURIComponent(code.trim())}`)
    }

    setIsLoading(false)
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={placeholder}
          required
          disabled={isLoading}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            border: '1px solid var(--input-border)',
            borderRadius: '0.375rem',
            backgroundColor: 'var(--input-background)',
            color: 'var(--input-foreground)',
            fontSize: '1rem',
            fontFamily: 'monospace',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        />
        <button
          type="submit"
          disabled={isLoading || !code.trim()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--primary-default)',
            color: 'var(--primary-foreground)',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: isLoading || !code.trim() ? 'not-allowed' : 'pointer',
            opacity: isLoading || !code.trim() ? 0.6 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {isLoading ? '...' : buttonText}
        </button>
      </div>
    </form>
  )
}
